import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlexboxGrid } from 'rsuite';
import PortalHeader from '../../components/layout/PortalHeader';
import {
  createAccount,
  terminateSession,
} from '../../store/slices/auth/authThunks';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCreateAccountEmailAddress,
  selectIsAccountCreationCompleted,
  selectIsAuthCodeRequired,
  selectWorkErrorMessage,
  selectWorkFailed,
  selectWorkProgressMessage,
  selectWorkStarted,
  selectWorkSuccess,
} from '../../store/slices/auth/authSelectors';
import Notice from '../../components/user-feedback/Notice';
import AuthCodeModal from '../../components/auth/AuthCodeModal';
import {
  logout,
  resetWorkProgressState,
  setAuthCodeRequired,
} from '../../store/slices/auth/authSlice';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import PersonDetails from '../../components/form/PersonDetails';
import {
  companyNameValidation,
  nameRequiredValidation,
  phoneRequiredValidation,
  requiredValidation,
} from '../../common/validation';
import { FLOWS_ENUM } from '../../common/enums';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors';
import { titleize } from '../../common/utils';
import { clearPersistedState } from '../../common/tokenPersistence';

const CreateAccount = () => {
  // Dependencies and state
  const form = useRef();
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});
  const [addressComponents, setAddressComponents] = useState({});

  // Selectors
  const isCreateAccountStarted = useSelector(selectWorkStarted);
  const isCreateAccountSucceeded = useSelector(selectWorkSuccess);
  const isCreateAccountFailed = useSelector(selectWorkFailed);
  const createAccountProgressMsg = useSelector(selectWorkProgressMessage);
  const createAccountErrorMsg = useSelector(selectWorkErrorMessage);
  const emailAddress = useSelector(selectCreateAccountEmailAddress);
  const showAuthModal = useSelector(selectIsAuthCodeRequired);
  const isAccountCreationCompleted = useSelector(
    selectIsAccountCreationCompleted
  );

  const selectedApplicationPermitVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );

  const formModel = useMemo(() => {
    return {
      email: emailAddress ?? '',
      firstName: '',
      lastName: '',
      phone: '',
      companyName: '',
      mailingAddress: '',
    };
  }, [emailAddress]);

  useEffect(() => {
    setFormValues({ ...formModel });
  }, [formModel, setFormValues]);

  // Validation
  const validationModel = {
    // don't need email validation because it can't be changed
    firstName: nameRequiredValidation,
    lastName: nameRequiredValidation,
    companyName: companyNameValidation,
    phone: phoneRequiredValidation,
    mailingAddress: requiredValidation,
  };

  // On mount
  useEffect(() => {
    if (isAccountCreationCompleted === true) {
      placeNavigate('/dashboard');
      dispatch(resetWorkProgressState());
    }
  }, [isAccountCreationCompleted, dispatch, placeNavigate]);

  // Functions
  const createAccountValues = useMemo(
    () => ({
      ...(formValues || {}),
      mailingAddressComponents: addressComponents || {},
    }),
    [formValues, addressComponents]
  );

  const onCreateAccount = () => {
    if (!form.current.check()) return;
    dispatch(createAccount(createAccountValues));
  };

  const onAuthModalReject = () => {
    // on cancel or close of modal
    // AT this point the account has already been created, but they have not enetred their mfa to login
    // So navigate to the login page
    dispatch(setAuthCodeRequired(false));
    dispatch(logout());
    dispatch(terminateSession());
    clearPersistedState();
    placeNavigate('/');
  };

  const onUnrestrictedAddressSelected = ({ components }) => {
    setAddressComponents(components);
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader
          flow={FLOWS_ENUM.CREATE_ACCOUNT}
          showAccountDropdown={false}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as="main"
        colspan={24}
      >
        <FlowPageLayout
          title="Personal Details"
          onNext={onCreateAccount}
          subtitle={`Please enter your details below to create your account. These will be used as the ${titleize(selectedApplicationPermitVerbiage)} Holder information for any ${selectedApplicationPermitVerbiage.toLowerCase()}s you apply for.`}
        >
          <PersonDetails
            ref={form}
            setFormError={setFormError}
            setFormValues={(values) => {
              setFormValues((prev) => ({
                ...prev,
                ...values,
              }));
            }}
            formModel={formModel}
            formError={formError}
            validationModel={validationModel}
            formValues={formValues}
            showEmail={false}
            emailDisabled={true}
            onUnrestrictedAddressLookupChange={(address) =>
              onUnrestrictedAddressSelected(address)
            }
          />

          {isCreateAccountStarted ? (
            <IconIndicator text={createAccountProgressMsg} />
          ) : null}
          {isCreateAccountSucceeded ? (
            <Notice
              type="success"
              message={createAccountProgressMsg}
              iconSize={16}
              messageStyle={{ fontSize: 12 }}
              className="mv-12"
            />
          ) : null}
          {isCreateAccountFailed === true && (
            <Notice
              type="error"
              message={createAccountErrorMsg}
              iconSize={16}
              messageStyle={{ fontSize: 12 }}
              className="mv-12"
            />
          )}
        </FlowPageLayout>
      </FlexboxGrid.Item>
      <AuthCodeModal
        showModal={showAuthModal}
        onModalReject={onAuthModalReject}
        usage="create_account"
      />
    </FlexboxGrid>
  );
};

export default CreateAccount;
